import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";

export default function NewsDetail() {
  const { newsId } = useParams<{ newsId: string }>();
  const news = newsList.find((n) => n.id === newsId);

  if (!news) {
    return (
      <div className="w-full px-4 md:px-6 pt-32 pb-20 min-h-[70vh] flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-file-unknow-line" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
          新闻不存在或已删除
        </h1>
        <p className="mt-2 text-sm text-foreground-600">该资讯可能已被移除，或链接地址有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = newsList
    .filter((n) => n.id !== news.id && n.category === news.category)
    .slice(0, 3);
  const fallbackRelated = newsList.filter((n) => n.id !== news.id).slice(0, 3);
  const recommendations = related.length > 0 ? related : fallbackRelated;

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-8 md:pb-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300 mb-5">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <Link to="/news" className="hover:text-accent-400 transition-colors">
              资讯新闻
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <span className="text-background-100">正文</span>
          </nav>
          <span className="inline-block rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            {news.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight max-w-4xl">
            {news.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-background-200">
            <span className="inline-flex items-center gap-2">
              <i className="ri-user-line text-accent-400" aria-hidden="true"></i>
              {news.author}
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-calendar-line text-accent-400" aria-hidden="true"></i>
              {news.date}
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-time-line text-accent-400" aria-hidden="true"></i>
              约 {news.readMinutes} 分钟阅读
            </span>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <article className="lg:col-span-8">
            <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-100 mb-6">
              <img
                src={news.cover}
                alt={news.title}
                title={`${news.title} 欧冠资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <p className="text-base md:text-lg font-medium text-foreground-800 leading-relaxed border-l-4 border-accent-500 pl-4 mb-6">
              {news.summary}
            </p>

            <div className="flex flex-col gap-4">
              {news.content.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-sm md:text-base text-foreground-700 leading-loose"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-1">标签：</span>
              {news.tags.map((t) => (
                <Link
                  key={t}
                  to="/news"
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 hover:bg-secondary-200 transition-colors"
                >
                  {t}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between gap-3">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 rounded-md border border-background-200 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
                返回资讯列表
              </Link>
              <Link
                to={`/schedule/${"2025-26"}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                查看赛程
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                相关新闻推荐
              </h2>
              <div className="flex flex-col gap-4">
                {recommendations.map((n) => (
                  <Link
                    key={n.id}
                    to={`/news/${n.id}`}
                    className="group flex gap-3"
                  >
                    <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-100">
                      <img
                        src={n.cover}
                        alt={n.title}
                        title={`${n.title} 欧冠资讯`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {n.title}
                      </h3>
                      <span className="mt-1 block text-xs text-foreground-500">{n.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}